<template>
  <v-container fluid>
    <v-row justify="center">
      <v-col cols="12" lg="11" xl="10">
        <v-alert v-if="!isUserAdmin" type="error" outlined class="mt-8">
          You are not allowed to access SAPAPI diagnostics.
        </v-alert>

        <v-card v-else class="mt-8 mb-8" :loading="loading">
          <template slot="progress">
            <v-progress-linear color="#006c27" indeterminate height="6"></v-progress-linear>
          </template>
          <v-card-title class="grey--text text--darken-2">
            SAPAPI Portal Diagnostics
            <v-spacer></v-spacer>
            <SAPAPIStatusOverride
              :override="status.override"
              @changed="onOverrideChanged"
              @error="showError"
            ></SAPAPIStatusOverride>
            <v-btn text color="#006c27" class="ml-2" @click="loadData">Reload</v-btn>
          </v-card-title>

          <v-card-text>
            <v-alert v-if="status.override && status.override.status" dense outlined type="warning">
              A global {{ status.override.status }} override is active. Reason: {{ status.override.reason }}
              <span v-if="status.override.updatedBy"> Set by {{ status.override.updatedBy }}.</span>
            </v-alert>

            <v-data-table
              :headers="headers"
              :items="portalRows"
              :items-per-page="6"
              hide-default-footer
              class="elevation-1"
            >
              <template v-slot:[`item.actualStatus`]="{ item }">
                <v-chip x-small dark :color="statusColor(item.actualStatus)">{{ item.actualStatus }}</v-chip>
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <v-chip x-small dark :color="statusColor(item.status)">{{ item.status }}</v-chip>
              </template>
              <template v-slot:[`item.checkedAt`]="{ item }">
                {{ formatDate(item.lastProbe || item.checkedAt) }}
              </template>
              <template v-slot:[`item.responseCode`]="{ item }">
                {{ valueOrDash(item.responseCode) }}
              </template>
              <template v-slot:[`item.consecutiveSuccesses`]="{ item }">
                {{ valueOrDash(item.consecutiveSuccesses) }}
              </template>
              <template v-slot:[`item.lastOutcome`]="{ item }">
                {{ formatOutcome(item.lastOutcome) }}
              </template>
              <template v-slot:[`item.latency`]="{ item }">
                {{ latencySummary(item.latency) }}
              </template>
              <template v-slot:[`item.failure`]="{ item }">
                {{ item.failure || item.reason || "-" }}
              </template>
              <template v-slot:[`item.session`]="{ item }">
                {{ sessionSummary(item) }}
              </template>
              <template slot="no-data">No portal status is available yet.</template>
            </v-data-table>

            <v-divider class="my-6"></v-divider>
            <div class="d-flex flex-wrap align-center mb-3">
              <div class="text-h6 grey--text text--darken-2 mr-4">Actual Portal Status History</div>
              <v-btn-toggle v-model="historyWindow" mandatory color="#006c27">
                <v-btn small value="15m">15m</v-btn>
                <v-btn small value="1h">1h</v-btn>
                <v-btn small value="6h">6h</v-btn>
              </v-btn-toggle>
            </div>
            <div class="text-caption grey--text text--darken-1 mb-3">
              Source: {{ history.source || "-" }}
              <span class="mx-1">|</span>
              Started: {{ formatDate(history.startedAt) }}
              <span class="mx-1">|</span>
              Retention: {{ history.retention || "-" }}
            </div>
            <v-alert v-if="!historyLoading && filteredHistory.length === 0" dense outlined type="info">
              No local status history is available for the selected period. History is retained only by the running CASS instance.
            </v-alert>
            <v-chart v-else class="history-chart" :option="historyOption" autoresize></v-chart>

            <template v-if="filteredHistory.length > 0">
              <v-divider class="my-6"></v-divider>
              <div class="d-flex flex-wrap align-center mb-3">
                <div class="text-h6 grey--text text--darken-2 mr-4">Portal Latency</div>
                <v-btn-toggle v-model="latencyMetric" mandatory color="#006c27">
                  <v-btn small value="latest">Latest</v-btn>
                  <v-btn small value="p50">p50</v-btn>
                  <v-btn small value="p95">p95</v-btn>
                  <v-btn small value="max">Max</v-btn>
                </v-btn-toggle>
                <v-spacer></v-spacer>
                <v-btn
                  outlined
                  small
                  color="#006c27"
                  class="ml-4"
                  href="https://monitor.dev.api.schaeffler/d/ad2wxhh/sap-apis-monitoring?from=now-24h&to=now&timezone=browser"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Grafana
                  <v-icon right small>mdi-open-in-new</v-icon>
                </v-btn>
              </div>
              <div class="text-caption grey--text text--darken-1 mb-3">
                {{ latencyMetricDescription }} Failed probes contribute the timeout ceiling, so spikes show outages; the red reference line marks the 5 second threshold.
                Failed Latest samples are marked by outcome: red circle for timeout, orange diamond for transport error, and amber square for non-2xx.
              </div>
              <v-chart class="history-chart" :option="latencyOption" autoresize></v-chart>
            </template>

            <v-divider class="my-6"></v-divider>
            <div class="text-h6 grey--text text--darken-2 mb-3">Recent Override Audit</div>
            <v-data-table
              :headers="auditHeaders"
              :items="audits"
              :items-per-page="5"
              hide-default-footer
              class="elevation-1"
            >
              <template v-slot:[`item.status`]="{ item }">
                <v-chip x-small outlined :color="item.status ? statusColor(item.status) : '#546e7a'">
                  {{ item.status || "CLEAR" }}
                </v-chip>
              </template>
              <template v-slot:[`item.at`]="{ item }">
                {{ formatDate(item.at) }}
              </template>
              <template v-slot:[`item.actualStates`]="{ item }">
                {{ auditActualStates(item) }}
              </template>
              <template slot="no-data">No override changes have been recorded.</template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <v-snackbar v-model="snackbar.visible" :color="snackbar.color" timeout="4000">
          {{ snackbar.text }}
        </v-snackbar>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart } from "echarts/charts";
import {
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TooltipComponent,
} from "echarts/components";
import VChart, { THEME_KEY } from "vue-echarts";
import cass from "../api/cass";
import SAPAPIStatusOverride from "../components/SAPAPIStatusOverride.vue";
import {
  historyEntriesForWindow,
  portalIsActuallyOnline,
  portalLatencyValue,
  portalProbeOutcome,
  SAPAPI_ENVIRONMENTS,
  SAPAPI_PORTALS,
} from "../utils/sapapiDiagnostics";

const FAILURE_MARKERS = {
  timeout: { color: "#c62828", symbol: "circle" },
  transport_error: { color: "#ef6c00", symbol: "diamond" },
  non_2xx: { color: "#f9a825", symbol: "rect" },
};

use([CanvasRenderer, LineChart, GridComponent, LegendComponent, MarkLineComponent, TooltipComponent]);

export default {
  name: "SAPAPIDiagnostics",
  components: { SAPAPIStatusOverride, VChart },
  provide: { [THEME_KEY]: "light" },
  data() {
    return {
      loading: false,
      historyLoading: false,
      status: { portals: [], override: null },
      history: { startedAt: null, retention: "", source: "", entries: [] },
      historyWindow: "1h",
      latencyMetric: "p95",
      headers: [
        { text: "Environment", value: "environment", sortable: false },
        { text: "Portal", value: "portalLabel", sortable: false },
        { text: "Actual", value: "actualStatus", sortable: false },
        { text: "Effective", value: "status", sortable: false },
        { text: "Reason", value: "failure", sortable: false },
        { text: "Last probe", value: "checkedAt", sortable: false },
        { text: "HTTP", value: "responseCode", sortable: false },
        { text: "Successes", value: "consecutiveSuccesses", sortable: false },
        { text: "Latest outcome", value: "lastOutcome", sortable: false },
        { text: "Latency (latest / p50 / p95 / max)", value: "latency", sortable: false },
        { text: "Session", value: "session", sortable: false },
      ],
      auditHeaders: [
        { text: "Action", value: "action", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "Reason", value: "reason", sortable: false },
        { text: "Actor", value: "actor", sortable: false },
        { text: "At", value: "at", sortable: false },
        { text: "Actual states", value: "actualStates", sortable: false },
      ],
      snackbar: { visible: false, text: "", color: "#b71c1c" },
    };
  },
  computed: {
    isUserAdmin() {
      return !!(this.$userData && this.$userData.cass && this.$userData.cass.isUserAdmin);
    },
    portalRows() {
      const portals = Array.isArray(this.status.portals) ? this.status.portals : [];
      return SAPAPI_ENVIRONMENTS.flatMap((environment) =>
        SAPAPI_PORTALS.map((portal) => {
          const item = portals.find(
            (entry) => entry.environment === environment && entry.portal === portal
          );
          return {
            environment,
            portal,
            portalLabel: this.portalLabel(portal),
            actualStatus: "-",
            status: "-",
            ...item,
          };
        })
      );
    },
    filteredHistory() {
      const minutes = { "15m": 15, "1h": 60, "6h": 360 }[this.historyWindow];
      return historyEntriesForWindow(this.history, minutes);
    },
    audits() {
      const audits = Array.isArray(this.status.audits) ? this.status.audits : [];
      return audits.map((audit) => ({
        ...audit,
        action: audit.action || (audit.status ? "SET" : "CLEARED"),
      }));
    },
    latencyMetricDescription() {
      if (this.latencyMetric === "latest") return "Raw latency of the most recent portal probe.";
      return `${this.latencyMetric} latency for the fast rolling probe window.`;
    },
    historyOption() {
      const seriesKeys = SAPAPI_ENVIRONMENTS.flatMap((environment) =>
        SAPAPI_PORTALS.map((portal) => ({ environment, portal, name: `${environment} ${this.portalLabel(portal)}` }))
      );
      return {
        color: ["#1565c0", "#00897b", "#f9a825", "#ef6c00", "#6a1b9a", "#c62828"],
        tooltip: { trigger: "axis" },
        legend: { type: "scroll", bottom: 0 },
        grid: { left: 82, right: 18, top: 24, bottom: 62 },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: this.filteredHistory.map((snapshot) => this.formatChartDate(snapshot.at)),
        },
        yAxis: {
          type: "value",
          min: 0,
          max: 1,
          interval: 1,
          axisLabel: { formatter: (value) => (value ? "ONLINE" : "DEGRADED") },
        },
        series: seriesKeys.map((key) => ({
          name: key.name,
          type: "line",
          step: "end",
          showSymbol: false,
          data: this.filteredHistory.map((snapshot) => {
            const portal = (snapshot.portals || []).find(
              (entry) => entry.environment === key.environment && entry.portal === key.portal
            );
            return portalIsActuallyOnline(portal) ? 1 : 0;
          }),
        })),
      };
    },
    latencyOption() {
      const seriesKeys = SAPAPI_ENVIRONMENTS.flatMap((environment) =>
        SAPAPI_PORTALS.map((portal) => ({ environment, portal, name: `${environment} ${this.portalLabel(portal)}` }))
      );
      const latencyPoint = (snapshot, key) => {
        const value = portalLatencyValue(snapshot, key.environment, key.portal, this.latencyMetric);
        if (value === null) return null;
        return { value, outcome: portalProbeOutcome(snapshot, key.environment, key.portal) };
      };
      return {
        color: ["#1565c0", "#00897b", "#f9a825", "#ef6c00", "#6a1b9a", "#c62828"],
        tooltip: {
          trigger: "axis",
          formatter: (points) => {
            const entries = Array.isArray(points) ? points : [points];
            const lines = entries.map((point) => {
              const outcome = point.data && point.data.outcome;
              const outcomeText = outcome ? ` (${this.formatOutcome(outcome)})` : "";
              return `${point.marker}${point.seriesName}: ${point.value} ms${outcomeText}`;
            });
            return [entries[0] && entries[0].axisValueLabel, ...lines].join("<br/>");
          },
        },
        legend: { type: "scroll", bottom: 0 },
        grid: { left: 56, right: 18, top: 24, bottom: 62 },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: this.filteredHistory.map((snapshot) => this.formatChartDate(snapshot.at)),
        },
        yAxis: { type: "value", name: "ms" },
        series: seriesKeys.map((key, index) => {
          const data = this.filteredHistory.map((snapshot) => latencyPoint(snapshot, key));
          const failureMarkers = data
            .map((point, sampleIndex) => {
              const marker = point && FAILURE_MARKERS[point.outcome];
              if (!marker || this.latencyMetric !== "latest") return null;
              return {
                coord: [sampleIndex, point.value],
                itemStyle: { color: marker.color },
                symbol: marker.symbol,
                symbolSize: 10,
              };
            })
            .filter(Boolean);
          return {
            name: key.name,
            type: "line",
            connectNulls: false,
            showSymbol: false,
            data,
            markPoint: failureMarkers.length > 0 ? { data: failureMarkers } : undefined,
            markLine:
              index === 0
                ? {
                    symbol: "none",
                    label: { formatter: "5 s threshold" },
                    lineStyle: { color: "#c62828", type: "dashed" },
                    data: [{ yAxis: 5000 }],
                  }
                : undefined,
          };
        }),
      };
    },
  },
  mounted() {
    if (this.isUserAdmin) {
      this.loadData();
    }
  },
  methods: {
    portalLabel(portal) {
      return portal === "devportal" ? "Dev Portal" : "API Portal";
    },
    statusColor(status) {
      if (status === "ONLINE") return "#2e7d32";
      if (status === "WARNING") return "#ef6c00";
      return "#c62828";
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
    },
    formatChartDate(value) {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? value : date.toLocaleTimeString();
    },
    valueOrDash(value) {
      return value === null || value === undefined || value === "" ? "-" : value;
    },
    formatOutcome(outcome) {
      return outcome ? outcome.replace(/_/g, " ") : "-";
    },
    latencySummary(latency) {
      if (!latency) return "-";
      const values = [latency.latest, latency.p50, latency.p95, latency.max];
      if (values.every((value) => value === null || value === undefined)) return "-";
      return values
        .map((value) => (value === null || value === undefined ? "-" : `${value} ms`))
        .join(" / ");
    },
    sessionSummary(item) {
      const session = item.session;
      if (!session) return "-";
      return [
        `primed: ${session.primed ? "yes" : "no"}`,
        `token: ${(session.tokenSet ?? session.token_set) ? "set" : "missing"}`,
        `cookies: ${this.valueOrDash(session.cookieCount ?? session.cookie_count)}`,
        `rotations: ${this.valueOrDash(session.rotationCount ?? session.rotation_count)}`,
      ].join(", ");
    },
    auditActualStates(audit) {
      const states = audit.actualStates || audit.snapshot || [];
      if (!Array.isArray(states) || states.length === 0) return "-";
      const unhealthy = states.filter((state) => state.status !== "ONLINE");
      if (unhealthy.length === 0) return "All portals ONLINE";
      return unhealthy
        .map((state) => `${state.environment} ${this.portalLabel(state.portal)}: ${state.status}`)
        .join("; ");
    },
    showError(message) {
      this.snackbar = { visible: true, text: message, color: "#b71c1c" };
    },
    onOverrideChanged(override) {
      this.status = { ...this.status, override };
      this.loadData();
    },
    async loadData() {
      if (!this.isUserAdmin) return;
      this.loading = true;
      this.historyLoading = true;
      try {
        const [statusResponse, historyResponse] = await Promise.all([
          cass.getAdminSAPAPIStatus(),
          cass.getAdminSAPAPIStatusHistory(),
        ]);
        this.status = statusResponse.data || { portals: [], override: null };
        this.history = historyResponse.data || {
          startedAt: null,
          retention: "",
          source: "",
          entries: [],
        };
      } catch (error) {
        this.showError(cass.handleError(error).message);
      } finally {
        this.loading = false;
        this.historyLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.history-chart {
  width: 100%;
  height: 380px;
}
</style>
